import { Link } from "@/src/common/components/Link"
import { cachedApiCall } from "@/src/common/utils/cachedApiCall"
import {
  URLFactory,
  appendSlash
} from "@/src/sb/business-layer/factories/URLFactory"
import { Features, SidebarMenuItemV2 } from "@/src/sb/business-layer/types"
import { useAnalytics } from "@/src/sb/components/Analytics"
import AngleDown from "@/src/sb/components/icon/AngleDown"
import X from "@/src/sb/components/icon/X"
import { StearsLogoNode } from "@/src/sb/components/layouts/DefaultLayoutV3"
import { OPEN_DATA_PAGE_BASE_PATH } from "@/src/sb/constants/configs"
import {
  SIDEBAR_COUNTRIES_CLASS_NAME,
  SIDEBAR_INDUSTRIES_CLASS_NAME,
  SIDEBAR_MAX_DEPTH
} from "@/src/sb/constants/featureFlags"
import { SBAPIInstance } from "@/src/sb/data-layer"
import { useWindowSize } from "@/src/sb/hooks/useWindowSize"
import classNames from "classnames"
import { useRouter } from "next/router"
import { ReactNode, useEffect, useMemo, useState } from "react"
import useSWR from "swr"

type SidebarV2Props = {
  onClose?: () => void
  asideFooter?: ReactNode | null
  sidebarMenuItems: { [key: string]: SidebarMenuItemV2[] }
  sidebarRootItemsToHide: Array<number>
  features?: Features
}

const SIDEBAR_OPEN_STATE = "sidebarOpenState"

const SidebarV2 = ({
  onClose,
  asideFooter = null,
  sidebarMenuItems,
  sidebarRootItemsToHide,
  features
}: SidebarV2Props) => {
  const updatedSidebarMenuItems = {
    ...sidebarMenuItems
  }

  const tagClassificationNames: Record<string, any> = {
    countries: features?.[SIDEBAR_COUNTRIES_CLASS_NAME],
    industries: features?.[SIDEBAR_INDUSTRIES_CLASS_NAME]
  }

  const analytics = useAnalytics()
  const [isMobile] = useWindowSize()
  const router = useRouter()
  const [sidebarOpenState, setSidebarOpenState] = useState<
    Record<string, boolean>
  >({})

  const { data: countryList } = useSWR(
    ["countries_list", { limit: 54, offset: 0 }],
    () =>
      cachedApiCall("countries-list", () =>
        SBAPIInstance.fetchCountries({ limit: 54, offset: 0 })
      ),
    {
      revalidateOnFocus: false,
      dedupingInterval: 300000 // 5 minutes
    }
  )

  const availableCountrySlugs = useMemo(
    () => countryList?.items?.map((country: any) => country.slug) || [],
    [countryList]
  )

  const persistSidebarOpenState = (openState: Record<string, boolean>) => {
    setSidebarOpenState(openState)
    localStorage.setItem(SIDEBAR_OPEN_STATE, JSON.stringify(openState))
  }

  const getDestination = (
    menuItem: Pick<SidebarMenuItemV2, "destination" | "slug">,
    classification: string
  ) =>
    menuItem.destination
      ? appendSlash(menuItem.destination)
      : classification === "countries" &&
        availableCountrySlugs.includes(menuItem.slug)
      ? URLFactory.country(menuItem.slug)
      : `/search?tags=${menuItem.slug}`

  const hasCurrentRoute = (
    menuItem: SidebarMenuItemV2,
    classification: string
  ) => {
    const destination = getDestination(menuItem, classification)

    return (
      (router.asPath.startsWith("/wf")
        ? router.asPath.replace("/wf", "")
        : router.asPath) === destination ||
      (router.query.tags as string) === menuItem.slug
    )
  }

  const renderSidebarMenuItemChildren = (
    sidebarMenuItems: Array<SidebarMenuItemV2> | undefined,
    classification: string,
    depth = 1,
    maxDepth = Number(features?.[SIDEBAR_MAX_DEPTH])
  ) => {
    return sidebarMenuItems?.length
      ? sidebarMenuItems.map((sidebarMenuItemChild: SidebarMenuItemV2) => {
          if (!sidebarMenuItemChild.isSidebarItem) return null
          const hasChildren = sidebarMenuItemChild.children?.filter(
            (item) => item.isSidebarItem
          ).length

          const menuItemGeneratedKey: string =
            sidebarMenuItemChild.id + "-" + sidebarMenuItemChild.name

          const item = (
            <>
              {sidebarMenuItemChild.name}{" "}
              {hasChildren && depth < maxDepth ? (
                <span
                  className={classNames(
                    "inline-block shrink-0 transition-all",
                    { "-rotate-90": !sidebarOpenState[menuItemGeneratedKey] }
                  )}
                >
                  <AngleDown />
                </span>
              ) : null}
            </>
          )

          const itemProps = {
            className: classNames(
              "pr-5 cursor-pointer flex items-center gap-4 justify-between",
              "transition-all dark:hover:bg-[#1A1D21] dark:hover:text-stears_white hover:!no-underline dark:hover:no-underline",
              hasCurrentRoute(sidebarMenuItemChild, classification)
                ? "!text-gray-900 dark:!text-stears_white dark:bg-[#1A1D21]"
                : "!text-gray-700 dark:!text-[#9CA3AF]",
              {
                "text-sm leading-[22px] font-medium pl-5 py-[5px]": depth === 1,
                "text-xs leading-[16px] py-[6px] py-[6px]": depth > 1,
                "pl-[30px]": depth === 2,
                "pl-[40px]": depth === 3,
                "pl-[50px]": depth === 4,
                "text-stears_white dark:text-stears_white dark:text-stears_white ":
                  sidebarMenuItemChild?.slug === "screener"
              }
            )
          }

          return (
            <section key={menuItemGeneratedKey}>
              {hasChildren && depth < maxDepth ? (
                <section
                  {...itemProps}
                  onClick={() =>
                    persistSidebarOpenState({
                      ...sidebarOpenState,
                      [menuItemGeneratedKey]:
                        !sidebarOpenState[menuItemGeneratedKey]
                    })
                  }
                >
                  {item}
                </section>
              ) : (
                <Link
                  {...itemProps}
                  href={getDestination(sidebarMenuItemChild, classification)}
                  onClick={() => {
                    onClose?.()

                    analytics?.clickNavItem("menu_item", {
                      label: sidebarMenuItemChild.name,
                      destination: getDestination(
                        sidebarMenuItemChild,
                        classification
                      ),
                      context: "sidebar"
                    })
                  }}
                >
                  <span
                    className={classNames(
                      "block transition-all rounded-[3px]",
                      sidebarMenuItemChild?.isHighlightedSidebarItem
                        ? "bg-gray-700 text-stears_white dark:bg-stears_white dark:text-black font-semibold px-2 py-1 shadow-md text-sm"
                        : ""
                    )}
                  >
                    {item}
                  </span>
                </Link>
              )}

              {sidebarOpenState[menuItemGeneratedKey] && depth < maxDepth
                ? renderSidebarMenuItemChildren(
                    sidebarMenuItemChild.children,
                    classification,
                    depth + 1,
                    maxDepth
                  )
                : null}
            </section>
          )
        })
      : null
  }

  useEffect(() => {
    const persistedSidebarOpenState = localStorage.getItem(SIDEBAR_OPEN_STATE)
    if (persistedSidebarOpenState === null) return
    setSidebarOpenState(JSON.parse(persistedSidebarOpenState))
  }, [])

  return (
    <>
      <section
        className={
          "flex gap-4 justify-between items-center p-5 border-b-[0.5px] dark:border-gray-600 shrink-0 h-[60px] md:h-[80px] sticky md:static top-0 left-0 right-0 z-20 leading-[20px]"
        }
      >
        <StearsLogoNode useSVG height={21.3} />

        <button
          onClick={onClose}
          className={
            "flex justify-center items-center dark:bg-gray-600 w-6 h-6 md:hidden ml-auto"
          }
        >
          <X size={20} />
        </button>
      </section>

      <section className="h-[calc(100vh-60px)] md:h-[calc(100vh-80px)] flex flex-col relative">
        <section className={"grow flex flex-col overflow-auto no-scrollbar"}>
          {Object.keys(updatedSidebarMenuItems)
            .filter((_class, idx) => {
              if (
                _class !== "open data" &&
                router.asPath.includes(OPEN_DATA_PAGE_BASE_PATH)
              )
                return false

              if (!isMobile || !sidebarRootItemsToHide.includes(idx))
                return true
            })
            .map((_class) => {
              const classLabel = tagClassificationNames?.[_class] ?? _class
              const itemProps = {
                className: classNames(
                  "pl-4 flex gap-2 items-center text-gray-700",
                  "dark:text-gray-400 border-l-4 border-l-transparent",
                  "transition-all sticky top-0 z-20 bg-stears_white",
                  "dark:bg-[#0E0F10] leading-[12px] font-normal",
                  "pt-[10px] text-[10px] h-8 capitalize"
                )
              }

              return (
                <section
                  key={classLabel}
                  className={classNames(
                    "border-b-[0.5px] dark:border-gray-600 last-of-type:border-none font-jakarta",
                    "pb-5"
                  )}
                >
                  <section {...itemProps}>{classLabel}</section>

                  <section>
                    {renderSidebarMenuItemChildren(
                      updatedSidebarMenuItems[_class],
                      _class,
                      1,
                      _class === "open data"
                        ? 5
                        : Number(features?.[SIDEBAR_MAX_DEPTH])
                    )}
                  </section>
                </section>
              )
            })}
        </section>

        <section className={"sticky bottom-0 px-5"}>{asideFooter}</section>
      </section>
    </>
  )
}

export default SidebarV2
